import { call, select, CallEffect, SelectEffect } from 'redux-saga/effects';
import { getApiKey, getApiOption, getEnv, getOrchestratorHost } from 'common/selectors';
import { getLang } from 'utils';
import { api, ApiRequestConfig } from '../api';
import { KV } from '../types/utils';

export function* post<Response, Data extends Record<string, unknown> = NonNullable<unknown>>(
  path: string,
  data: Data,
  config?: ApiRequestConfig,
  queryParams?: Record<string, string> | null,
  customHost?: string,
): Generator<unknown, Response, Response> {
  const params = queryParams ? new URLSearchParams(queryParams || '').toString() : '';
  const resultPath = params ? `${path}?${params}` : path;
  return yield* doRequest<Response, Data>('post', resultPath, data, config, customHost);
}

export function* put<Response, Data extends object = Record<string, unknown>>(
  path: string,
  data: Data,
  config?: ApiRequestConfig,
  customHost?: string,
): Generator<unknown, Response, Response> {
  return yield* doRequest<Response, Data>('put', path, data, config, customHost);
}

export function* get<Response, Data extends object = Record<string, unknown>>(
  path: string,
  data: Data,
  config: ApiRequestConfig = {},
  customHost?: string,
) {
  return yield* doRequest<Response, Data>('get', path, data, config, customHost);
}

export function* del<Response = unknown, Data extends object = Record<string, unknown>>(
  path: string,
  data: Data,
  config: ApiRequestConfig = {},
  customHost?: string,
) {
  const lang: string = yield call(getLang);
  return yield* doRequest<Response, Data>('delete', path, { ...data, lang }, config, customHost);
}

function* doRequest<Response, Data>(
  method: 'get' | 'post' | 'put' | 'delete',
  path: string,
  data: Data,
  config: ApiRequestConfig = {},
  customHost?: string,
): Generator<CallEffect<unknown> | SelectEffect, Response> {
  const { USE_CREDENTIALS, requestID } = (yield select(getEnv)) as ReturnType<typeof getEnv>;
  const orchestratorHost = (yield select(getOrchestratorHost)) as ReturnType<typeof getOrchestratorHost>;
  const apiKey = (yield select(getApiKey)) as ReturnType<typeof getApiKey>;
  const apiOption = (yield select(getApiOption)) as ReturnType<typeof getApiOption>;
  const lang = yield call(getLang);
  const host = customHost ? customHost : orchestratorHost;

  config.withCredentials = USE_CREDENTIALS;

  if (requestID) {
    config.headers = { ...config.headers, 'x-request-id': requestID };
  }

  if (apiKey) {
    config.headers = { ...config.headers, 'x-api-key': apiKey };
  }

  if (apiOption) {
    config.headers = { ...config.headers, 'x-api-option': apiOption };
  }
  const isPostPutRequest = method === 'post' || method === 'put';
  const apiMethod = api[method];
  const url = buildUrl(host || '', path, isPostPutRequest ? { lang } : null);

  return yield call(apiMethod, url, isPostPutRequest ? data : { ...data, lang }, config);
}

export function buildUrl(base: string, part: string, params?: KV | null) {
  const searchParams = new URLSearchParams(params || {});
  const resultUrl = new URL(part, base);

  searchParams.forEach((value, key) => resultUrl.searchParams.set(key, value));

  return resultUrl.toString();
}
